import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Percent, Search } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountsTaxRates = () => {
  const [rates, setRates] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", rate: "", type: "GST" });
  const [search, setSearch] = useState("");

  const fetch = useCallback(async () => {
    const r = await axios.get(`${API}/accounts/tax-rates`);
    setRates(r.data);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    if (!form.name || !form.rate) { toast.error("Name and rate required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/accounts/tax-rates/${editing.id}`, form);
        toast.success("Tax rate updated");
      } else {
        await axios.post(`${API}/accounts/tax-rates`, form);
        toast.success("Tax rate created");
      }
      setShowDialog(false);
      setEditing(null);
      setForm({ name: "", rate: "", type: "GST" });
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this tax rate?")) return;
    await axios.delete(`${API}/accounts/tax-rates/${id}`);
    toast.success("Deleted");
    fetch();
  };

  const filtered = rates.filter(r => r.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="tax-rates-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-blue-400 flex items-center gap-2"><Percent className="w-5 h-5" /> Tax Rates</h2>
          <p className="text-gray-400 text-sm">Manage GST tax rates</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setForm({ name: "", rate: "", type: "GST" }); setShowDialog(true); }}
          className="bg-blue-600 hover:bg-blue-700" data-testid="add-tax-rate-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Tax Rate
        </Button>
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="bg-gray-800 border-gray-600 text-white pl-9 h-9" />
      </div>

      <Card className="bg-gray-900/30 border-gray-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700">
                <TableHead className="text-gray-400">Name</TableHead>
                <TableHead className="text-gray-400">Rate (%)</TableHead>
                <TableHead className="text-gray-400">Type</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-gray-500 py-8">No tax rates. Click "Add Tax Rate" to create one.</TableCell></TableRow>
              ) : filtered.map(r => (
                <TableRow key={r.id} className="border-gray-800">
                  <TableCell className="text-white font-medium">{r.name}</TableCell>
                  <TableCell className="text-cyan-400 font-mono">{r.rate}%</TableCell>
                  <TableCell className="text-gray-300">{r.type}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8 px-2"
                      onClick={() => { setEditing(r); setForm({ name: r.name, rate: String(r.rate), type: r.type }); setShowDialog(true); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 px-2" onClick={() => handleDelete(r.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader><DialogTitle className="text-blue-400">{editing ? "Edit" : "Add"} Tax Rate</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-gray-300">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., GST 18%" className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div><Label className="text-gray-300">Rate (%)</Label><Input type="number" step="0.01" value={form.rate} onChange={e => setForm({...form, rate: e.target.value})} placeholder="e.g., 18" className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
            <div><Label className="text-gray-300">Type</Label><Input value={form.type} onChange={e => setForm({...form, type: e.target.value})} placeholder="GST" className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-tax-rate-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsTaxRates;
